import {
    LogOutIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/common/DropdownMenu';
import Tooltip from '@/components/common/Tooltip';
import { Button } from '@/components/common/Button';
import Avatar from '@/components/common/Avatar';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { getAvatarFallbackName, isRoles } from '@/utils/utils';
import { ekDomain, ROUTES } from '@/configs/constants';
import { isPathEk } from '@/utils/routes';
import { useEffect } from 'react';
import { MY_ACCOUNT_TABS } from '@/pages/my-account';

export default observer(function UserAvatar() {
    // hooks
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // store
    const {
        generalStore: { getMyAccount, myAccount },
        authStore: { logout, profileGas, profileEk },
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    useEffect(() => {
        getMyAccount();
    }, []);

    const handleLogout = () => {
        const idModal = 'confirm-logout-modal';
        showAlertModal({
            id: idModal,
            size: 'sm',
            content: t('messages.confirm_logout'),
            isConfirmModal: true,
            saveButton: t('button.sign_out'),
            onSave: () => {
                hideModal(idModal);
                logout();
            }
        });
    };

    return (
        <DropdownMenu>
            <Tooltip
                content={t('words_title.profile')}
                triggerAsChild={true}
                side='bottom'
                align='end'
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        variant='outline'
                        className='relative h-8 w-8 rounded-full'
                    >
                        <Avatar
                            src={myAccount?.avartarImage?.path || ''}
                            fallback={getAvatarFallbackName(
                                isPathEk(pathname) ? profileEk?.fullname : profileGas?.fullname
                            )}
                        />
                    </Button>
                </DropdownMenuTrigger>
            </Tooltip>

            <DropdownMenuContent className='w-56' align='end' forceMount>
                <DropdownMenuLabel className='font-normal'>
                    <div className='flex flex-col space-y-1'>
                        <p className='text-sm font-medium leading-none'>
                            {isPathEk(pathname) ? profileEk?.fullname : profileGas?.fullname}
                        </p>
                        <p className='text-xs leading-none text-muted-foreground'>
                            {isPathEk(pathname) ? profileEk?.email : profileGas?.email}
                        </p>
                    </div>
                </DropdownMenuLabel>
                {MY_ACCOUNT_TABS.filter(
                    tab => tab.roles.length === 0 || isRoles(tab.roles)
                ).map(tab => (
                    <DropdownMenuGroup key={tab.pathName}>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className='hover:cursor-pointer' asChild>
                            <Link
                                to={
                                    isPathEk(pathname) ?
                                        `/${ekDomain}${ROUTES.myAccount.href}/${tab.pathName}` :
                                        `${ROUTES.myAccount.href}/${tab.pathName}`
                                }
                                className='flex items-center'
                            >
                                {tab.icon}
                                {t(tab.label)}
                            </Link>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                ))}
                <DropdownMenuSeparator />

                <DropdownMenuItem
                    className='hover:cursor-pointer'
                    onClick={() => {
                        handleLogout();
                        document.body.style.pointerEvents = 'auto';
                    }}
                >
                    <LogOutIcon className='w-4 h-4 mr-2' />
                    {t('words_title.sign_out')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
});
